import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { API } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useProperty } from "@/context/PropertyContext";
import {
  User, Building2, Coins, Bell, Shield, Download, Trash2,
  Mail, ExternalLink, Wifi, Phone, Clock, MapPin, Image as ImageIcon, Save,
} from "lucide-react";

const TABS = [
  { key: "account", icon: User, label: "Account" },
  { key: "property", icon: Building2, label: "Struttura" },
  { key: "tax", icon: Coins, label: "Tassa di soggiorno" },
  { key: "notifications", icon: Bell, label: "Notifiche" },
  { key: "privacy", icon: Shield, label: "Privacy & GDPR" },
];

export default function Settings() {
  const [tab, setTab] = useState("account");
  return (
    <div className="max-w-6xl mx-auto" data-testid="settings-page">
      <div className="px-6 py-6">
        <h1 className="font-heading font-semibold text-3xl tracking-tight">Impostazioni</h1>
        <p className="text-slate-600 text-sm mt-1">Tutte le configurazioni della tua attività in un unico posto.</p>
      </div>

      <div className="grid lg:grid-cols-12 gap-6 px-6 pb-12">
        {/* SIDEBAR TABS */}
        <nav className="lg:col-span-3" data-testid="settings-sidebar">
          <div className="bg-white border border-slate-200 rounded-lg p-2 lg:sticky lg:top-6">
            {TABS.map(({ key, icon: Icon, label }) => (
              <button
                key={key}
                onClick={() => setTab(key)}
                data-testid={`settings-tab-${key}`}
                className={`w-full flex items-center gap-3 px-4 py-3 text-left text-sm rounded-lg mb-1 transition-colors ${
                  tab === key
                    ? "bg-blue-700 text-white font-medium"
                    : "text-slate-700 hover:bg-slate-50"
                }`}
              >
                <Icon className="w-4 h-4 flex-shrink-0" strokeWidth={1.6} />
                {label}
              </button>
            ))}
          </div>
        </nav>

        {/* CONTENT */}
        <section className="lg:col-span-9">
          <div className="bg-white border border-slate-200 rounded-lg p-6 lg:p-8 min-h-[500px]">
            {tab === "account" && <AccountTab />}
            {tab === "property" && <PropertyTab />}
            {tab === "tax" && <TaxTab />}
            {tab === "notifications" && <NotificationsTab />}
            {tab === "privacy" && <PrivacyTab />}
          </div>
        </section>
      </div>
    </div>
  );
}

/* ---------- TAB: ACCOUNT ---------- */
function AccountTab() {
  const { user } = useAuth();
  return (
    <div data-testid="tab-account">
      <Title icon={User}>Account</Title>
      <Field label="Nome completo" value={user?.full_name || "—"} />
      <Field label="Email" value={user?.email || "—"} />
      <p className="text-xs text-slate-500 mt-6">
        Per modificare nome o email, contatta l'assistenza: <a href="mailto:support@xeniaregistry.com" className="text-blue-700 underline-offset-2 hover:underline">support@xeniaregistry.com</a>
      </p>
    </div>
  );
}

/* ---------- TAB: PROPERTY (data + logo + check-in/out + wifi) ---------- */
function PropertyTab() {
  const { current, refresh } = useProperty();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (current) setForm(current); }, [current]);
  if (!form) return <p className="text-slate-500 text-sm">Caricamento…</p>;

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSave = async () => {
    setSaving(true);
    try {
      await api.put(`/properties/${form.id}`, {
        name: form.name, address: form.address, phone: form.phone, email: form.email,
        logo_url: form.logo_url,
        checkin_from: form.checkin_from, checkin_to: form.checkin_to, checkout_time: form.checkout_time,
        wifi_ssid: form.wifi_ssid, wifi_password: form.wifi_password,
      });
      toast.success("Struttura aggiornata");
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore");
    } finally { setSaving(false); }
  };

  return (
    <div data-testid="tab-property">
      <Title icon={Building2}>Struttura · {form.name}</Title>

      <Section icon={MapPin} title="Anagrafica">
        <Input label="Nome struttura" value={form.name || ""} onChange={set("name")} testid="prop-name" />
        <Input label="Indirizzo completo" value={form.address || ""} onChange={set("address")} placeholder="Via Roma 12, 22100 Como" testid="prop-address" />
        <div className="grid sm:grid-cols-2 gap-4">
          <Input label="Telefono reception" value={form.phone || ""} onChange={set("phone")} placeholder="+39 031 123456" testid="prop-phone" icon={Phone} />
          <Input label="Email contatto" value={form.email || ""} onChange={set("email")} placeholder="info@hotel.it" testid="prop-email" icon={Mail} />
        </div>
      </Section>

      <Section icon={ImageIcon} title="Logo struttura">
        <LogoUploader
          value={form.logo_url}
          onChange={(url) => setForm({ ...form, logo_url: url })}
        />
        <p className="text-xs text-slate-500">Apparirà in alto su tutte le email automatiche. Formati: PNG / JPG / WEBP, max 500 KB.</p>
      </Section>

      <Section icon={Clock} title="Orari">
        <div className="grid sm:grid-cols-3 gap-4">
          <Input label="Check-in dalle" type="time" value={form.checkin_from || "14:00"} onChange={set("checkin_from")} testid="prop-checkin-from" />
          <Input label="Check-in fino a" type="time" value={form.checkin_to || "22:00"} onChange={set("checkin_to")} testid="prop-checkin-to" />
          <Input label="Check-out entro" type="time" value={form.checkout_time || "11:00"} onChange={set("checkout_time")} testid="prop-checkout" />
        </div>
      </Section>

      <Section icon={Wifi} title="Wi-Fi (incluso nelle email di benvenuto)">
        <div className="grid sm:grid-cols-2 gap-4">
          <Input label="Nome rete" value={form.wifi_ssid || ""} onChange={set("wifi_ssid")} testid="prop-wifi-ssid" />
          <Input label="Password" value={form.wifi_password || ""} onChange={set("wifi_password")} testid="prop-wifi-pwd" />
        </div>
      </Section>

      <SaveBar onSave={onSave} saving={saving} />
    </div>
  );
}

/* ---------- TAB: TOURIST TAX ---------- */
function TaxTab() {
  const { current, refresh } = useProperty();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (current) setForm(current); }, [current]);
  if (!form) return <p className="text-slate-500 text-sm">Caricamento…</p>;

  const set = (k, parser = (v) => v) => (e) => setForm({ ...form, [k]: parser(e.target.value) });

  const onSave = async () => {
    setSaving(true);
    try {
      await api.put(`/properties/${form.id}`, {
        tourist_tax_rate: parseFloat(form.tourist_tax_rate) || 0,
        tourist_tax_max_nights: parseInt(form.tourist_tax_max_nights, 10) || 0,
        exempt_age_under: parseInt(form.exempt_age_under, 10) || 0,
        exempt_age_over: form.exempt_age_over ? parseInt(form.exempt_age_over, 10) : null,
      });
      toast.success("Tassa di soggiorno aggiornata");
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore");
    } finally { setSaving(false); }
  };

  const sample = (parseFloat(form.tourist_tax_rate) || 0) * 2 * Math.min(3, parseInt(form.tourist_tax_max_nights, 10) || 0);

  return (
    <div data-testid="tab-tax">
      <Title icon={Coins}>Tassa di soggiorno</Title>

      <Section title="Tariffa Comune">
        <div className="grid sm:grid-cols-2 gap-4">
          <Input label="Importo per persona / notte (€)" type="number" step="0.10"
            value={form.tourist_tax_rate ?? ""}
            onChange={set("tourist_tax_rate", parseFloat)} testid="tax-rate" />
          <Input label="Massimo notti tassabili" type="number" min="1" max="30"
            value={form.tourist_tax_max_nights ?? ""}
            onChange={set("tourist_tax_max_nights", parseInt)} testid="tax-max-nights" />
        </div>
        <p className="text-xs text-slate-600 mt-3">
          Le tariffe sono stabilite dal Comune. Verifica sul sito del tuo Comune o sulla delibera comunale.
        </p>
      </Section>

      <Section title="Esenzioni per età (default per la tua struttura)">
        <div className="grid sm:grid-cols-2 gap-4">
          <Input label="Esenti sotto i (anni)" type="number" min="0" max="100"
            value={form.exempt_age_under ?? 10}
            onChange={set("exempt_age_under", parseInt)} testid="tax-exempt-under" />
          <Input label="Esenti sopra i (anni, opzionale)" type="number" min="0" max="120"
            value={form.exempt_age_over ?? ""}
            onChange={set("exempt_age_over", (v) => v === "" ? null : parseInt(v, 10))} testid="tax-exempt-over" />
        </div>
        <p className="text-xs text-slate-600 mt-3">
          Esenzioni puntuali per singolo ospite (gite scolastiche, residenti del Comune, lavoratori in trasferta…) si possono impostare direttamente dalla scheda prenotazione.
        </p>
      </Section>

      <div className="bg-warm-amber border border-blue-200 p-4 rounded-lg">
        <p className="text-xs tracked text-blue-700 mb-2">SIMULAZIONE</p>
        <p className="text-sm text-stone-800">
          Una famiglia di 2 adulti che soggiorna 3 notti pagherà:{" "}
          <strong className="text-blue-800">€ {sample.toFixed(2)}</strong>
          <span className="text-slate-600"> di tassa di soggiorno.</span>
        </p>
      </div>

      <SaveBar onSave={onSave} saving={saving} />
    </div>
  );
}

/* ---------- TAB: NOTIFICATIONS ---------- */
function NotificationsTab() {
  const { current, refresh } = useProperty();
  const [channel, setChannel] = useState("sms");
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (current?.notification_channel) setChannel(current.notification_channel); }, [current]);
  if (!current) return <p className="text-slate-500 text-sm">Caricamento…</p>;

  const onSave = async () => {
    setSaving(true);
    try {
      await api.put(`/properties/${current.id}`, { notification_channel: channel });
      toast.success("Canale notifiche aggiornato");
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore");
    } finally { setSaving(false); }
  };

  const opts = [
    { v: "sms", title: "Solo SMS", desc: "Standard, raggiunge tutti. Costo Twilio." },
    { v: "whatsapp", title: "Solo WhatsApp", desc: "Più moderno, miglior leggibilità. Richiede l'opt-in dell'ospite." },
    { v: "both", title: "Entrambi", desc: "Prima WhatsApp, fallback SMS se non disponibile." },
  ];

  return (
    <div data-testid="tab-notifications">
      <Title icon={Bell}>Notifiche</Title>

      <Section title="Canale messaggi agli ospiti">
        <div className="space-y-3">
          {opts.map(o => (
            <label key={o.v} className={`flex items-start gap-3 p-4 border-2 rounded-lg cursor-pointer transition-colors ${
              channel === o.v ? "border-blue-700 bg-blue-50" : "border-slate-200 hover:border-slate-300"
            }`} data-testid={`notif-channel-${o.v}`}>
              <input type="radio" name="ch" value={o.v} checked={channel === o.v} onChange={(e) => setChannel(e.target.value)} className="mt-1" />
              <div>
                <div className="font-medium text-slate-900">{o.title}</div>
                <div className="text-sm text-slate-600">{o.desc}</div>
              </div>
            </label>
          ))}
        </div>
      </Section>

      <Section title="Template attivi">
        <ul className="grid sm:grid-cols-2 gap-2 text-sm text-slate-700">
          {[
            "Conferma prenotazione",
            "Promemoria check-in (24h prima)",
            "Istruzioni di arrivo",
            "Saluto post check-in",
            "Promemoria check-out",
            "Ringraziamento + recensione",
            "Cancellazione prenotazione",
            "Notifiche all'host",
          ].map(x => (
            <li key={x} className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-sage-dark" /> {x}
            </li>
          ))}
        </ul>
        <p className="text-xs text-slate-500 mt-3">Gli invii automatici programmati (24h prima, post-soggiorno) verranno attivati a breve. Gli invii manuali sono già operativi.</p>
      </Section>

      <SaveBar onSave={onSave} saving={saving} />
    </div>
  );
}

/* ---------- TAB: PRIVACY ---------- */
function PrivacyTab() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [consents, setConsents] = useState(null);
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/me/consents").then(({ data }) => setConsents(data)).catch(() => {}); }, []);

  const onToggle = async () => {
    setBusy(true);
    try {
      const next = !consents.marketing_accepted;
      await api.post("/me/consents/marketing", { accepted: next });
      setConsents(c => ({ ...c, marketing_accepted: next }));
      toast.success(next ? "Iscritto alla newsletter" : "Disiscritto dalla newsletter");
    } catch { toast.error("Errore"); }
    finally { setBusy(false); }
  };

  const onExport = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${API}/me/data-export`, { credentials: "include" });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `xenia-export-${user?.email}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Dati esportati");
    } catch { toast.error("Errore esportazione"); }
    finally { setBusy(false); }
  };

  const onDelete = async () => {
    if (confirmText !== "ELIMINA") { toast.error('Devi scrivere "ELIMINA"'); return; }
    setBusy(true);
    try {
      await api.delete("/me/account");
      toast.success("Account eliminato");
      await logout();
      nav("/", { replace: true });
    } catch { toast.error("Errore"); setBusy(false); }
  };

  return (
    <div data-testid="tab-privacy">
      <Title icon={Shield}>Privacy & GDPR</Title>

      <Section title="Comunicazioni di marketing">
        <label className="flex items-start gap-3 p-4 border border-slate-200 rounded-lg cursor-pointer hover:bg-slate-50">
          <input type="checkbox" checked={!!consents?.marketing_accepted} onChange={onToggle} disabled={busy || !consents} className="mt-1" data-testid="toggle-marketing" />
          <div>
            <div className="font-medium">Voglio ricevere aggiornamenti su nuove funzioni</div>
            <div className="text-sm text-slate-600">Massimo una email al mese. Puoi disiscriverti in qualsiasi momento.</div>
          </div>
        </label>
      </Section>

      <Section title="Esporta tutti i tuoi dati (art. 20 GDPR)">
        <p className="text-sm text-slate-600 mb-3">Riceverai un file JSON con account, strutture, prenotazioni, ospiti, fatture.</p>
        <Btn onClick={onExport} disabled={busy} icon={Download} testid="export-data">Scarica JSON</Btn>
      </Section>

      <Section title="Cancella account (art. 17 GDPR)">
        <p className="text-sm text-slate-600 mb-3">
          Cancella in modo permanente account e dati personali. I dati legalmente obbligatori (schedine TULPS, fatture)
          vengono pseudonimizzati e conservati per i tempi di legge.
        </p>
        <input
          type="text"
          placeholder='Scrivi "ELIMINA" per confermare'
          value={confirmText}
          onChange={(e) => setConfirmText(e.target.value)}
          className="border border-red-300 px-3 py-2 rounded-lg text-sm w-full sm:w-auto mr-2 mb-2"
          data-testid="delete-confirm-text"
        />
        <Btn onClick={onDelete} disabled={busy || confirmText !== "ELIMINA"} icon={Trash2} variant="danger" testid="delete-account">
          Elimina account
        </Btn>
      </Section>

      <p className="text-xs text-slate-500 mt-8">
        Per maggiori informazioni consulta la <a href="/privacy" className="text-blue-700 underline-offset-2 hover:underline inline-flex items-center gap-1">Informativa privacy <ExternalLink className="w-3 h-3" /></a>.
      </p>
    </div>
  );
}

/* ---------- atomic ui ---------- */
function Title({ icon: Icon, children }) {
  return (
    <h2 className="font-heading font-semibold text-2xl tracking-tight mb-6 inline-flex items-center gap-2">
      <Icon className="w-5 h-5 text-blue-700" strokeWidth={1.6} />
      {children}
    </h2>
  );
}
function Section({ title, icon: Icon, children }) {
  return (
    <div className="mb-8">
      <div className="text-xs tracked text-slate-500 mb-3 inline-flex items-center gap-1.5">
        {Icon && <Icon className="w-3 h-3" />}{title?.toUpperCase()}
      </div>
      <div className="space-y-3">{children}</div>
    </div>
  );
}
function Field({ label, value }) {
  return (
    <div className="flex items-baseline gap-3 py-2 border-b border-stone-100 last:border-0">
      <span className="text-xs text-slate-500 w-32 flex-shrink-0">{label}</span>
      <span className="text-sm text-slate-900">{value}</span>
    </div>
  );
}
function Input({ label, value, onChange, placeholder, type = "text", icon: Icon, testid, step, min, max }) {
  return (
    <label className="block">
      <span className="text-xs text-slate-600 mb-1 block">{label}</span>
      <div className="relative">
        {Icon && <Icon className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />}
        <input type={type} value={value} onChange={onChange} placeholder={placeholder} step={step} min={min} max={max}
          className={`border border-slate-300 px-3 py-2 rounded-lg text-sm w-full focus:border-blue-700 focus:ring-2 focus:ring-blue-100 outline-none ${Icon ? "pl-9" : ""}`}
          data-testid={testid} />
      </div>
    </label>
  );
}
function Btn({ children, onClick, disabled, icon: Icon, variant = "primary", testid }) {
  const cls = variant === "danger"
    ? "bg-red-700 text-white hover:bg-red-800 disabled:bg-stone-300"
    : "bg-blue-700 text-white hover:bg-blue-700 disabled:bg-stone-300";
  return (
    <button onClick={onClick} disabled={disabled} data-testid={testid}
      className={`inline-flex items-center gap-2 px-4 py-2 text-sm rounded-lg transition-colors ${cls}`}>
      {Icon && <Icon className="w-4 h-4" />}{children}
    </button>
  );
}
function SaveBar({ onSave, saving }) {
  return (
    <div className="flex justify-end pt-6 border-t border-slate-200 mt-8 sticky bottom-0 bg-white">
      <Btn onClick={onSave} disabled={saving} icon={Save} testid="settings-save">
        {saving ? "Salvataggio…" : "Salva modifiche"}
      </Btn>
    </div>
  );
}

const MAX_LOGO_BYTES = 500 * 1024; // 500 KB
const ALLOWED_LOGO_TYPES = ["image/png", "image/jpeg", "image/webp"];

function LogoUploader({ value, onChange }) {
  const [hover, setHover] = useState(false);

  const handleFile = (file) => {
    if (!file) return;
    if (!ALLOWED_LOGO_TYPES.includes(file.type)) {
      toast.error("Formato non valido. Usa PNG, JPG o WEBP.");
      return;
    }
    if (file.size > MAX_LOGO_BYTES) {
      toast.error("File troppo grande. Massimo 500 KB.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      onChange(reader.result); // data URI base64
      toast.success("Logo caricato. Ricorda di salvare le modifiche.");
    };
    reader.onerror = () => toast.error("Errore lettura file");
    reader.readAsDataURL(file);
  };

  return (
    <div className="space-y-3">
      <div
        onDragOver={(e) => { e.preventDefault(); setHover(true); }}
        onDragLeave={() => setHover(false)}
        onDrop={(e) => { e.preventDefault(); setHover(false); handleFile(e.dataTransfer.files?.[0]); }}
        className={`relative border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
          hover ? "border-blue-700 bg-blue-50" : "border-slate-300 bg-slate-50"
        }`}
        data-testid="logo-dropzone"
      >
        {value ? (
          <div className="flex flex-col items-center gap-3">
            <img src={value} alt="logo struttura" className="max-h-20 max-w-[240px] bg-white p-2 border border-slate-200 rounded-lg" data-testid="logo-preview" />
            <div className="flex gap-2">
              <label className="text-xs text-blue-700 underline-offset-2 hover:underline cursor-pointer">
                Cambia
                <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden"
                  onChange={(e) => handleFile(e.target.files?.[0])} data-testid="logo-replace" />
              </label>
              <button
                onClick={() => { onChange(null); toast.success("Logo rimosso. Ricorda di salvare."); }}
                className="text-xs text-red-700 hover:underline"
                data-testid="logo-remove"
              >Rimuovi</button>
            </div>
          </div>
        ) : (
          <label className="cursor-pointer block">
            <ImageIcon className="w-10 h-10 text-slate-400 mx-auto mb-3" strokeWidth={1.4} />
            <div className="font-medium text-stone-800 mb-1">Trascina qui il tuo logo</div>
            <div className="text-xs text-slate-500 mb-3">o clicca per selezionarlo dal computer</div>
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-blue-700 text-white text-sm rounded-lg hover:bg-blue-700">
              Scegli file
            </span>
            <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden"
              onChange={(e) => handleFile(e.target.files?.[0])} data-testid="logo-upload" />
          </label>
        )}
      </div>
    </div>
  );
}
